import { createContext, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { adminMetricsController } from '@/api/generated/endpoints';
import type { RideGoalResponseDTO } from '@/api/generated/types';
import { unwrap } from '@/api/http';
import { useAuth } from '@/auth/AuthProvider';
import { TOAST_VISIBLE_MS, milestonesBetween, nextToastDelay, nextToastMarket, paceOf, percent, todayDeltas, type Pending } from './goalModel';

/**
 * The goal (every market's year to date and today, whatever market the header is set to). The
 * gateway answers it from memory/Redis only, so polling it is cheap; it is the only source of
 * the live toasts too (no ride searches from the browser).
 */
const GOAL_POLL_MS = 60_000;

export type GoalToast = { id: number; kind: 'rides'; market: string; count: number } | { id: number; kind: 'milestone'; value: number; leader: string | null };

/** A toast before it gets its id (Omit over each member of the union). */
type NewToast = GoalToast extends infer T ? (T extends GoalToast ? Omit<T, 'id'> : never) : never;

export interface TodayRow {
  name: string;
  today: number;
  /** Rank change since the previous update: 1 up, -1 down, 0 same or new. */
  move: -1 | 0 | 1;
}

export interface RideGoalState {
  year: number;
  target: number;
  /** Year-to-date rides; never steps back between updates. */
  total: number;
  pct: number;
  /** Rides ahead (+) or behind (−) the even pace to the target. */
  paceDelta: number;
  /** Where the bar should be by now, as a percentage of the target. */
  pacePct: number;
  /** Markets by year-to-date rides. */
  markets: { name: string; rides: number }[];
  /** Markets by today's rides. */
  today: TodayRow[];
  toast: GoalToast | null;
  dismissToast(): void;
  /** No year-to-date count yet (the gateway's first daily count is still running). */
  counting: boolean;
}

const RideGoalContext = createContext<RideGoalState | null>(null);

/** The ride goal, for the header bar and the live panel; null without the dynamic panel permission or before data. */
export const useRideGoal = () => useContext(RideGoalContext);

/**
 * Polls the goal once a minute and paces the live toasts: each market's rise in today's rides
 * between polls is queued, and released one market at a time every 20–40 s, markets taking
 * turns; crossing a milestone (every million, every tenth of the target) gets its own toast.
 */
export function RideGoalProvider({ children }: { children: ReactNode }) {
  const { permissions } = useAuth();
  const allowed = permissions.canView('dynamic_panel');
  const goal = useQuery({
    queryKey: ['live', 'ride-goal'],
    queryFn: async () => unwrap(await adminMetricsController.getRideGoal()) as RideGoalResponseDTO | null,
    enabled: allowed,
    refetchInterval: GOAL_POLL_MS,
  });
  const g = goal.data;

  // queue each market's new rides (today's count rose since the previous poll; the first load is the baseline)
  const pending = useRef(new Map<string, Pending>());
  const previousToday = useRef<Map<string, number> | null>(null);
  useEffect(() => {
    if (!g) return;
    const now = new Map((g.markets ?? []).filter((m) => m.name).map((m) => [m.name as string, m.today ?? 0]));
    const before = previousToday.current;
    previousToday.current = now;
    if (!before) return;
    for (const [name, count] of todayDeltas(before, now)) {
      const p = pending.current.get(name) ?? { count: 0, shownAt: 0 };
      pending.current.set(name, { ...p, count: p.count + count });
    }
  }, [g]);

  // the total never steps back (today's numbers can be recounted lower for a moment)
  const shown = useRef(0);
  if ((g?.total ?? 0) > shown.current) shown.current = g?.total ?? 0;
  const total = shown.current;
  const target = g?.target ?? 0;
  const year = g?.year ?? new Date().getFullYear();
  const today = useTodayBoard(g);

  const [toast, setToast] = useState<GoalToast | null>(null);
  const toastId = useRef(0);
  const hideTimer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const leader = useRef<string | null>(null);
  leader.current = today[0]?.name ?? null;
  const show = (t: NewToast) => {
    toastId.current += 1;
    setToast({ ...t, id: toastId.current } as GoalToast);
    clearTimeout(hideTimer.current);
    hideTimer.current = setTimeout(() => setToast(null), TOAST_VISIBLE_MS);
  };

  // milestones at once (not on the first load: they are something that happens while you watch)
  const lastTotal = useRef<number | null>(null);
  useEffect(() => {
    if (!g || target <= 0) return;
    const before = lastTotal.current;
    lastTotal.current = total;
    if (before === null) return;
    const crossed = milestonesBetween(before, total, target);
    if (crossed.length) show({ kind: 'milestone', value: crossed[crossed.length - 1]!, leader: leader.current });
  }, [total, target, g]);

  // one market's new rides every 20–40 s
  useEffect(() => {
    if (!allowed) return;
    let timer: ReturnType<typeof setTimeout>;
    const tick = () => {
      const market = nextToastMarket(pending.current);
      if (market) {
        const p = pending.current.get(market)!;
        pending.current.set(market, { count: 0, shownAt: Date.now() });
        show({ kind: 'rides', market, count: p.count });
      }
      timer = setTimeout(tick, nextToastDelay());
    };
    timer = setTimeout(tick, nextToastDelay());
    return () => {
      clearTimeout(timer);
      clearTimeout(hideTimer.current);
    };
  }, [allowed]);

  const value = useMemo<RideGoalState | null>(() => {
    if (!allowed || !g || target <= 0) return null;
    const pace = paceOf(total, target, year, new Date());
    const markets = (g.markets ?? [])
      .filter((m) => m.name && (m.rides ?? 0) > 0)
      .map((m) => ({ name: m.name as string, rides: m.rides ?? 0 }))
      .sort((a, b) => b.rides - a.rides);
    return {
      year,
      target,
      total,
      pct: percent(total, target),
      paceDelta: pace.delta,
      pacePct: percent(pace.expected, target),
      markets,
      today,
      toast,
      dismissToast: () => setToast(null),
      counting: g.countedThrough == null,
    };
  }, [allowed, g, target, total, year, today, toast]);

  return <RideGoalContext.Provider value={value}>{children}</RideGoalContext.Provider>;
}

/** Today's markets by rides, with each one's rank move since the previous update. */
function useTodayBoard(goal: RideGoalResponseDTO | null | undefined): TodayRow[] {
  const previous = useRef(new Map<string, number>());
  return useMemo(() => {
    const rows = (goal?.markets ?? [])
      .filter((m) => m.name && (m.today ?? 0) > 0)
      .map((m) => ({ name: m.name as string, today: m.today ?? 0 }))
      .sort((a, b) => b.today - a.today);
    const ranked = rows.map((r, i): TodayRow => {
      const before = previous.current.get(r.name);
      return { ...r, move: before === undefined || before === i ? 0 : before > i ? 1 : -1 };
    });
    previous.current = new Map(rows.map((r, i) => [r.name, i]));
    return ranked;
  }, [goal]);
}
